import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  BellRing, BellOff, CheckCheck, Loader2, Smartphone, Monitor, X,
  Send, ShieldCheck, AlertCircle, Stethoscope,
} from "lucide-react";
import api from "../lib/api";
import { enableWebPush, pushSupported } from "../lib/webpush";
import { toast } from "./ui/sonner";

/**
 * PushSubscriptionsCard — Wave 19 #84.
 *
 * Settings panel that:
 *  • shows the current device's push status (subscribed / not)
 *  • lets the user enable / refresh / send a test push from this device
 *  • lists every other device the user is subscribed on with a one-tap
 *    revoke so dead devices stop receiving notifications
 *
 * The user explicitly asked for this in the WhatsApp-parity push because
 * "the #1 reason WhatsApp wins is users trust the delivery" — they need
 * to be able to verify that THIS phone, right now, will deliver.
 */

function _ua(uaStr) {
  if (!uaStr) return { label: "Unknown device", Icon: Smartphone };
  const u = uaStr.toLowerCase();
  if (/ipad|tablet/.test(u)) return { label: "iPad / Tablet", Icon: Smartphone };
  if (/iphone/.test(u)) return { label: "iPhone", Icon: Smartphone };
  if (/android/.test(u)) {
    const m = uaStr.match(/Android[^;)]*/);
    return { label: m ? m[0] : "Android", Icon: Smartphone };
  }
  if (/windows/.test(u)) return { label: "Windows", Icon: Monitor };
  if (/macintosh|mac os/.test(u)) return { label: "Mac", Icon: Monitor };
  if (/linux/.test(u)) return { label: "Linux", Icon: Monitor };
  return { label: "Browser", Icon: Monitor };
}

function _fmt(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" });
  } catch { return ""; }
}

export default function PushSubscriptionsCard() {
  const [items, setItems] = useState([]);
  const [thisEndpoint, setThisEndpoint] = useState("");
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const supported = pushSupported();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/push/subscriptions");
      setItems(data?.items || []);
    } catch (_e) {
      // noop — show empty state
    } finally {
      setLoading(false);
    }
    if (supported) {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          setThisEndpoint(sub?.endpoint || "");
        }
      } catch { /* noop */ }
      if (typeof Notification !== "undefined") {
        setPermission(Notification.permission);
      }
    }
  }, [supported]);

  useEffect(() => { load(); }, [load]);

  const enable = async () => {
    setBusy("__enable__");
    try {
      await enableWebPush();
      toast.success("Push notifications enabled on this device.");
      await load();
    } catch (e) {
      toast.error(e?.message || "Could not enable push notifications.");
    } finally {
      setBusy("");
    }
  };

  const sendTest = async () => {
    setBusy("__test__");
    try {
      await api.post("/push/test");
      toast.success("Test push sent — should arrive in a moment.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test push.");
    } finally {
      setBusy("");
    }
  };

  const revoke = async (item) => {
    setBusy(item.endpoint);
    try {
      await api.post("/push/unsubscribe", { endpoint: item.endpoint });
      toast.success("Device removed.");
      // If the revoked one was THIS device, clear local subscription too.
      if (item.endpoint === thisEndpoint) {
        try {
          const reg = await navigator.serviceWorker.getRegistration();
          const sub = await reg?.pushManager.getSubscription();
          if (sub) await sub.unsubscribe();
          setThisEndpoint("");
        } catch { /* noop */ }
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove device.");
    } finally {
      setBusy("");
    }
  };

  const active = items.filter((i) => i.active !== false);
  const isThisDeviceSubscribed = thisEndpoint && active.some((a) => a.endpoint === thisEndpoint) && permission === "granted";

  if (!supported) {
    return (
      <div className="hv-card p-5 border-amber-400/30" data-testid="push-card">
        <div className="flex items-start gap-3">
          <BellOff className="w-5 h-5 text-amber-300 mt-0.5" />
          <div>
            <h3 className="font-display text-lg font-bold">Push notifications</h3>
            <p className="text-xs text-[#a0aabf] mt-1">
              This browser does not support web push notifications. On Apple devices, install the app to your Home Screen
              first — push works inside the installed PWA on iOS 16.4+.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hv-card p-5" data-testid="push-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-rose-500/15 border border-rose-500/40 flex items-center justify-center shrink-0">
          <BellRing className="w-5 h-5 text-rose-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-display text-2xl font-bold">Push notifications</h3>
            {isThisDeviceSubscribed ? (
              <span
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200"
                data-testid="push-this-device-status"
              >
                <ShieldCheck className="w-3 h-3" /> Active on this device
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200"
                data-testid="push-this-device-status"
              >
                <AlertCircle className="w-3 h-3" /> Not subscribed here
              </span>
            )}
          </div>
          <p className="text-sm text-[#a0aabf] mt-1">
            Get instant alerts from job cards, tickets, and the customer portal — even when the app is closed.
          </p>

          {permission === "denied" && (
            <div
              className="mt-3 text-xs text-rose-200 border border-rose-500/30 bg-rose-500/10 rounded px-3 py-2"
              data-testid="push-permission-denied"
            >
              Push permission was previously denied for this site. Open your browser's site settings and re-allow notifications,
              then come back here.
            </div>
          )}

          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="push-card-actions">
            {!isThisDeviceSubscribed && (
              <button
                onClick={enable}
                disabled={busy === "__enable__" || permission === "denied"}
                className="hv-btn hv-btn-primary text-xs disabled:opacity-40"
                data-testid="push-enable-btn"
              >
                {busy === "__enable__" ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
                Enable on this device
              </button>
            )}
            <Link
              to="/settings/notifications"
              className="hv-btn hv-btn-ghost text-xs !text-amber-300"
              title="Notifications not coming through? Run the diagnostic + step-by-step fixer."
              data-testid="push-diagnose-link"
            >
              <Stethoscope className="w-3 h-3" /> Notifications not arriving? Diagnose
            </Link>
            <button
              onClick={sendTest}
              disabled={busy === "__test__" || active.length === 0}
              className="hv-btn hv-btn-ghost text-xs disabled:opacity-40"
              data-testid="push-test-btn"
              title={active.length === 0 ? "No active devices to send to" : "Fires a test push to every active subscription"}
            >
              {busy === "__test__" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send test push
            </button>
            <button
              onClick={load}
              disabled={loading}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="push-refresh"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCheck className="w-3 h-3" />}
              Refresh
            </button>
          </div>

          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-2">
              Subscribed devices ({active.length})
            </div>
            {loading ? (
              <div className="text-xs text-[#a0aabf]">Loading…</div>
            ) : active.length === 0 ? (
              <div
                className="text-xs text-[#a0aabf] border border-dashed border-white/10 rounded p-3 text-center"
                data-testid="push-empty"
              >
                No active subscriptions yet. Tap "Enable on this device" to get started.
              </div>
            ) : (
              <ul className="divide-y divide-white/5 border border-white/10 rounded-md overflow-hidden">
                {active.map((s) => {
                  const ua = _ua(s.user_agent);
                  const isThis = s.endpoint === thisEndpoint;
                  const Icon = ua.Icon;
                  return (
                    <li
                      key={s.endpoint}
                      className={`flex items-center gap-3 p-3 ${isThis ? "bg-emerald-500/[0.06]" : ""}`}
                      data-testid={`push-device-${s.endpoint.slice(-12)}`}
                    >
                      <div className="w-8 h-8 rounded-md border border-white/10 bg-white/[0.03] flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-[#a0aabf]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white truncate">
                          {ua.label}
                          {isThis && (
                            <span className="ml-2 text-[9px] uppercase tracking-[0.2em] text-emerald-300 font-bold">This device</span>
                          )}
                        </div>
                        <div className="text-[10px] text-[#647087] truncate">
                          Subscribed {_fmt(s.created_at)} · Last update {_fmt(s.updated_at)}
                        </div>
                      </div>
                      <button
                        onClick={() => revoke(s)}
                        disabled={busy === s.endpoint}
                        className="hv-btn hv-btn-ghost text-[10px] text-rose-200 hover:text-rose-100 disabled:opacity-40"
                        data-testid={`push-revoke-${s.endpoint.slice(-12)}`}
                      >
                        {busy === s.endpoint ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                        Revoke
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
